import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Alert from 'components/common/alert/alert'
import { distributePercents } from '../../../../../core/utils/distribute-percents'
import { StoreTrajectoryContext } from '../../state/store-trajectory-context'
import CardSalesChannels, { type SalesChannelsPercents } from './card-sales-channels'
import CardTypeOfEconomy, { type TypeOfEconomyPercents } from './card-type-of-economy'
import type { Instore } from '../../../../../core/api/trajectory-photo/useApiGetStoreForecast'
import CardCircular, { type CircularPercents } from './card-circular'
import { VpButton } from '@vtmn-play/react'

export interface InstoreGmvSegmentProps {
  instore: Instore
}

const areNumbersEqual = (a: number, b: number) => {
  if (a === b) return true
  if (!a || !b) return false
  return a.toFixed(2) === b.toFixed(2)
}

const computeChannelPercent = (amount: number | null, total: number | null) => {
  if (amount == null || !total) {
    return null
  }
  return (amount * 100) / total
}

const InstoreGmvSegment = ({ instore }: InstoreGmvSegmentProps) => {
  const { t } = useTranslation()
  const {
    referenceYear,
    forecastInstoreGmv,
    forecastInstorePhysical,
    forecastInstoreDigital,
    forecastInstoreDecapro,
    forecastInstoreCircular,
    forecastInstoreNonCircular,
    forecastInstoreRepair,
    forecastInstoreSecondLife,
    forecastInstoreBuyBack,
    forecastInstoreRental,
    areInstoreGmvSegmentsTotalUnderAllocated,
    areInstoreGmvSegmentsTotalOverAllocated
  } = useContext(StoreTrajectoryContext)

  const [childrenKey, setChildrenKey] = useState(Date.now())

  // ----------------------------------------------
  // Sales channels
  const [physical, digital, decapro] = distributePercents([
    instore.reference_physical_gmv,
    instore.reference_digital_gmv,
    instore.reference_decapro_gmv
  ])
  const salesChannelsInitialPercents: SalesChannelsPercents = { physical, digital, decapro }

  // ----------------------------------------------
  // Type of economy
  const [circular, nonCircular] = distributePercents([
    instore.reference_eco_circular_gmv,
    instore.reference_eco_non_circular_gmv
  ])
  const typeOfEconomyInitialPercents: TypeOfEconomyPercents = { circular, nonCircular }

  // ----------------------------------------------
  // Circular
  const [repairAndMaintenance, savedProducts, buyBack, rental] = distributePercents([
    instore.reference_eco_circular_repair_and_maintenance_gmv,
    instore.reference_eco_circular_saved_products_gmv,
    instore.reference_eco_circular_buy_back_gmv,
    instore.reference_eco_circular_rental_gmv
  ])
  const circularInitialPercents: CircularPercents = { repairAndMaintenance, savedProducts, buyBack, rental }

  // ---------------------------------------------
  // Allocation alert
  const isTotalOverAllocated = areInstoreGmvSegmentsTotalOverAllocated()
  const isTotalUnderAllocated = areInstoreGmvSegmentsTotalUnderAllocated()
  const showAllocationAlert = isTotalOverAllocated || isTotalUnderAllocated

  const alertText = isTotalOverAllocated
    ? t('general.trajectory.store.edit.instore_gmv_segment.sales_channels.total_over_allocated')
    : t('general.trajectory.store.edit.instore_gmv_segment.sales_channels.total_under_allocated')

  // ---------------------------------------------
  // Reset button
  const isSameAsInit = (newValue: number, parentGmvValue: number, initialPercentageValue: number) => {
    const newPercentageValue = computeChannelPercent(newValue, parentGmvValue)
    return areNumbersEqual(newPercentageValue ?? 0, initialPercentageValue ?? 0)
  }

  const isSegmentForecastingIdle = () => {
    return (
      isSameAsInit(forecastInstorePhysical, forecastInstoreGmv, salesChannelsInitialPercents.physical) &&
      isSameAsInit(forecastInstoreDigital, forecastInstoreGmv, salesChannelsInitialPercents.digital) &&
      isSameAsInit(forecastInstoreDecapro, forecastInstoreGmv, salesChannelsInitialPercents.decapro) &&
      isSameAsInit(forecastInstoreCircular, forecastInstoreGmv, typeOfEconomyInitialPercents.circular) &&
      isSameAsInit(forecastInstoreNonCircular, forecastInstoreGmv, typeOfEconomyInitialPercents.nonCircular) &&
      isSameAsInit(forecastInstoreRepair, forecastInstoreCircular, circularInitialPercents.repairAndMaintenance) &&
      isSameAsInit(forecastInstoreSecondLife, forecastInstoreCircular, circularInitialPercents.savedProducts) &&
      isSameAsInit(forecastInstoreBuyBack, forecastInstoreCircular, circularInitialPercents.buyBack) &&
      isSameAsInit(forecastInstoreRental, forecastInstoreCircular, circularInitialPercents.rental)
    )
  }

  const resetSegmentForecasting = () => {
    // Resetting the children key forces the child components to re-render with their initial props, effectively resetting their state.
    setChildrenKey(Date.now())
  }

  return (
    <div className="mg-instore-gmv-segment">
      <div className="mg-instore-gmv-segment-header">
        <div className="mg-instore-gmv-segment-header-text">
          <div className="mg-instore-gmv-segment-header-text-title vp-subtitle-l">
            {t('general.trajectory.store.edit.instore_gmv_segment.title')}
          </div>
          <div className="mg-instore-gmv-segment-header-text-subtitle vp-body-m">
            {t('general.trajectory.store.edit.instore_gmv_segment.subtitle', { referenceYear })}
          </div>
        </div>

        <div className="mg-instore-gmv-segment-header-button">
          <VpButton
            disabled={isSegmentForecastingIdle()}
            data-testid="store-trajectory-edit-reset"
            variant="secondary"
            onClick={resetSegmentForecasting}
          >
            {t('general.trajectory.store.edit.reset')}
          </VpButton>
        </div>
      </div>

      {showAllocationAlert && (
        <div className="mg-instore-gmv-segment-alert" data-testid="instore-gmv-segment-alert">
          <Alert severity="negative" icon={{ name: 'close-circle', style: 'FILL' }} text={alertText} />
        </div>
      )}

      <div className="mg-instore-gmv-segment-body">
        <div className="mg-instore-gmv-segment-body-content">
          <CardSalesChannels key={`cardSalesChannels-${childrenKey}`} initialPercents={salesChannelsInitialPercents} />
          <CardTypeOfEconomy key={`cardTypeOfEconomy-${childrenKey}`} initialPercents={typeOfEconomyInitialPercents} />
          <CardCircular key={`cardCircular-${childrenKey}`} initialPercents={circularInitialPercents} />
        </div>
      </div>
    </div>
  )
}

export default InstoreGmvSegment
